import type { CadNode } from '@puppycad/proto'
import { CadFileKind } from '@puppycad/proto'
import type {
  CadNodeLoadSnapshot,
  NodeReloadApiAdapter,
  ReloadOpenProjectNodeInput,
} from './types/project-model-loading-types'
import { resolveCadNodeBackendTreeExpansionPolicy } from './project-model-backend-tree-expansion-policy'

/**
 * 解析 OpenProject root 加载前补齐输入。
 */
export interface ResolveLoadableOpenProjectRootCadNodeInput {
  /**
   * OpenProject 返回的根节点；缺失时表示当前仍走旧文件加载链路。
   */
  readonly rootNode: CadNode | null
  /** OpenProject 响应级总归档包；存在时无需补读 children。 */
  readonly archiveFile?: PuppyCADWeb.CadArchiveFileSnapshot | null
  /**
   * 已有 GetCadNodeTree 适配器，用于 root 没有离线包且缺 children 时补完整子树。
   */
  readonly nodeReloadAdapter?: NodeReloadApiAdapter
}

/**
 * 将可按装配加载的 OpenProject root 规整为装配节点。
 *
 * 设计说明：历史快照里可能出现 `PART + _assembly.json`，但 mapper 会严格禁止 PART
 * 携带装配清单。resolver 是进入 mapper 前的边界层，因此必须在这里收口类型语义，
 * 同时避免原地改写后端快照对象，方便调用方继续对比原始数据。
 *
 * @param rootNode 已确认应按装配 root 加载的 CAD 节点。
 * @returns 类型已规整为 ASSEMBLY 的 CAD root。
 */
function normalizeLoadableAssemblyRootCadNode(rootNode: CadNode): CadNode {
  if (rootNode.type === CadFileKind.ASSEMBLY) {
    return rootNode
  }

  return {
    ...rootNode,
    type: CadFileKind.ASSEMBLY,
  }
}

/**
 * 将 OpenProject root 解析为可直接映射的 CAD 节点。
 *
 * 设计说明：无 `.puppy` 的装配体或带 `_assembly.json` 的历史 root，必须以后端
 * children 结构作为右侧设计树来源。
 * 如果 OpenProject 首包只给 root 元信息和 `_assembly.json`，这里补调用一次 GetCadNodeTree；
 * 但命中 `.puppy` 的 root 必须跳过补树，让既有 `importPuppyPackage()` 链路解析包内
 * `assembly.json` 并生成权威 imported-model hierarchy。
 *
 * @param input OpenProject root 与可选节点重载能力。
 * @returns 可进入 `mapCadNodeToModelAssetNode` 的 root 节点与响应级归档快照。
 */
export async function resolveLoadableOpenProjectRootSnapshot(
  input: ResolveLoadableOpenProjectRootCadNodeInput,
): Promise<CadNodeLoadSnapshot | null> {
  const rootNode = input.rootNode

  if (!rootNode) {
    return null
  }

  if (input.archiveFile) {
    // assembly-single 总归档已完整承载当前模型，不能再因 CadNode.files 形态触发补树。
    return {
      // archiveFile 只决定加载方式，PART / ASSEMBLY 业务类型仍以 CadNode.type 为准。
      node: rootNode,
      archiveFile: input.archiveFile,
    }
  }

  const nodeReloadAdapter = input.nodeReloadAdapter
  const expansionPolicy = resolveCadNodeBackendTreeExpansionPolicy({
    rootNode,
    canReloadChildren: Boolean(nodeReloadAdapter),
  })

  if (expansionPolicy.kind === 'passthrough') {
    return { node: rootNode }
  }

  if (
    expansionPolicy.kind === 'offline-package-authoritative'
    || expansionPolicy.kind === 'children-tree'
  ) {
    return { node: normalizeLoadableAssemblyRootCadNode(rootNode) }
  }

  if (!nodeReloadAdapter) {
    return { node: normalizeLoadableAssemblyRootCadNode(rootNode) }
  }

  // OpenProject 根节点没有离线包且缺少 children 时，补读一次完整 CAD 节点树供首屏映射使用。
  const reloadInput: ReloadOpenProjectNodeInput = {
    nodeKey: 'open-project-root',
    repositoryId: rootNode.repositoryId.toString(),
    branchId: rootNode.branchId > 0n ? rootNode.branchId.toString() : undefined,
    commitId: rootNode.commitId.trim() || undefined,
  }
  const hydratedSnapshot = await nodeReloadAdapter.reloadSnapshot(reloadInput)

  return {
    node: normalizeLoadableAssemblyRootCadNode(hydratedSnapshot.node),
    ...(hydratedSnapshot.archiveFile
      ? { archiveFile: hydratedSnapshot.archiveFile }
      : {}),
  }
}
